import { DragEvent, FormEvent, useEffect, useId, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import {
  PlanningWorkItemEvidence,
  PlanningWorkItemEvidenceFormPayload,
  fetchPlanningWorkItemEvidenceImageUrl,
} from '../../services/strategicPlanningApi';
import { formatDateTimeBr } from '../../utils/formatDateBr';
import { DeleteIconButton } from '../ui/DeleteIconButton';
import { DraggableModalPanel } from '../ui/DraggableModalPanel';
import { EditIconButton } from '../ui/EditIconButton';
import { FieldLabel } from '../ui/FieldLabel';

type PlanningTaskEvidenceSectionProps = {
  taskId: string;
  evidences: PlanningWorkItemEvidence[];
  canWrite: boolean;
  busy: boolean;
  onAdd: (payload: PlanningWorkItemEvidenceFormPayload) => Promise<void>;
  onUpdate: (evidenceId: string, payload: PlanningWorkItemEvidenceFormPayload) => Promise<void>;
  onDelete: (evidenceId: string) => Promise<void>;
  loadImageUrl?: (taskId: string, evidenceId: string) => Promise<{ url: string }>;
};

const IMAGE_ACCEPT = '.png,.jpg,.jpeg,.gif,.webp,image/png,image/jpeg,image/gif,image/webp';
const IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp']);

export function PlanningTaskEvidenceSection({
  taskId,
  evidences,
  canWrite,
  busy,
  onAdd,
  onUpdate,
  onDelete,
  loadImageUrl = fetchPlanningWorkItemEvidenceImageUrl,
}: PlanningTaskEvidenceSectionProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editImage, setEditImage] = useState<File | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [composerOpen, setComposerOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    setTitle('');
    setDescription('');
    setExternalUrl('');
    setImage(null);
    setComposerOpen(false);
    setExpandedIds(new Set());
    setEditingId(null);
    setEditTitle('');
    setEditDescription('');
    setEditUrl('');
    setEditImage(null);
    setRemoveImage(false);
  }, [taskId]);

  async function handleAdd(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!title.trim() || !description.trim()) return;

    try {
      await onAdd({
        title: title.trim(),
        description: description.trim(),
        externalUrl: externalUrl.trim() || undefined,
        image,
      });
    } catch {
      return;
    }

    setTitle('');
    setDescription('');
    setExternalUrl('');
    setImage(null);
    setFormKey((current) => current + 1);
    setComposerOpen(false);
  }

  function closeComposer(): void {
    if (busy) return;
    setComposerOpen(false);
    setTitle('');
    setDescription('');
    setExternalUrl('');
    setImage(null);
    setFormKey((current) => current + 1);
  }

  async function handleUpdate(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!editingId || !editTitle.trim() || !editDescription.trim()) return;

    try {
      await onUpdate(editingId, {
        title: editTitle.trim(),
        description: editDescription.trim(),
        externalUrl: editUrl.trim() || undefined,
        image: editImage,
        removeImage: removeImage && !editImage,
      });
    } catch {
      return;
    }

    setEditingId(null);
    setEditImage(null);
    setRemoveImage(false);
  }

  function toggleExpanded(evidenceId: string): void {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(evidenceId)) next.delete(evidenceId);
      else next.add(evidenceId);
      return next;
    });
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-secondary-800">
          Evidências{evidences.length > 0 ? ` (${evidences.length})` : ''}
        </p>
        {canWrite ? (
          <button
            type="button"
            className="erp-btn-secondary"
            disabled={busy}
            onClick={() => setComposerOpen(true)}
          >
            + Evidência
          </button>
        ) : null}
      </div>

      {evidences.length === 0 ? (
        <p className="text-sm text-secondary-500">Nenhuma evidência registrada.</p>
      ) : (
        <ul className="space-y-2">
          {evidences.map((evidence) => (
            <li key={evidence.id} className="rounded-lg border border-secondary-200 text-sm">
              {editingId === evidence.id ? (
                <form className="space-y-2 p-2" onSubmit={(event) => void handleUpdate(event)}>
                  <EvidenceFields
                    title={editTitle}
                    description={editDescription}
                    externalUrl={editUrl}
                    imageName={editImage?.name ?? null}
                    busy={busy}
                    onTitleChange={setEditTitle}
                    onDescriptionChange={setEditDescription}
                    onUrlChange={setEditUrl}
                    onImageChange={setEditImage}
                  />
                  {evidence.storagePath && !editImage ? (
                    <label className="flex items-center gap-2 text-xs text-secondary-600">
                      <input
                        type="checkbox"
                        checked={removeImage}
                        disabled={busy}
                        onChange={(event) => setRemoveImage(event.target.checked)}
                      />
                      Remover a imagem atual
                    </label>
                  ) : null}
                  {evidence.storagePath && !removeImage ? (
                    <EvidenceImage
                      taskId={taskId}
                      evidenceId={evidence.id}
                      alt={evidence.title}
                      loadImageUrl={loadImageUrl}
                    />
                  ) : null}
                  <div className="erp-btn-actions">
                    <button
                      type="button"
                      className="erp-btn-secondary"
                      disabled={busy}
                      onClick={() => {
                        setEditingId(null);
                        setEditImage(null);
                        setRemoveImage(false);
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="erp-btn-primary"
                      disabled={busy || !editTitle.trim() || !editDescription.trim()}
                    >
                      Salvar
                    </button>
                  </div>
                </form>
              ) : (
                <EvidenceListItem
                  evidence={evidence}
                  taskId={taskId}
                  expanded={expandedIds.has(evidence.id)}
                  canWrite={canWrite}
                  busy={busy}
                  onToggle={() => toggleExpanded(evidence.id)}
                  onEdit={() => {
                    setEditingId(evidence.id);
                    setEditTitle(evidence.title ?? '');
                    setEditDescription(evidence.description ?? '');
                    setEditUrl(evidence.externalUrl ?? '');
                    setEditImage(null);
                    setRemoveImage(false);
                  }}
                  onDelete={() => void onDelete(evidence.id)}
                  loadImageUrl={loadImageUrl}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      <DraggableModalPanel
        open={composerOpen}
        title="Nova evidência"
        subtitle="Uma evidência por vez. Título e descrição são obrigatórios. A imagem é opcional."
        onClose={closeComposer}
        closeDisabled={busy}
        overlayClassName="!z-[160]"
        className="w-full max-w-lg"
        footer={(
          <div className="erp-btn-actions">
            <button type="button" className="erp-btn-secondary" disabled={busy} onClick={closeComposer}>
              Cancelar
            </button>
            <button
              type="submit"
              form="planning-evidence-composer"
              className="erp-btn-primary"
              disabled={busy || !title.trim() || !description.trim()}
            >
              Adicionar
            </button>
          </div>
        )}
      >
        <form
          id="planning-evidence-composer"
          key={formKey}
          className="space-y-2"
          onSubmit={(event) => void handleAdd(event)}
        >
          <EvidenceFields
            title={title}
            description={description}
            externalUrl={externalUrl}
            imageName={image?.name ?? null}
            busy={busy}
            onTitleChange={setTitle}
            onDescriptionChange={setDescription}
            onUrlChange={setExternalUrl}
            onImageChange={setImage}
          />
        </form>
      </DraggableModalPanel>
    </div>
  );
}

function EvidenceListItem({
  evidence,
  taskId,
  expanded,
  canWrite,
  busy,
  onToggle,
  onEdit,
  onDelete,
  loadImageUrl,
}: {
  evidence: PlanningWorkItemEvidence;
  taskId: string;
  expanded: boolean;
  canWrite: boolean;
  busy: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  loadImageUrl: (taskId: string, evidenceId: string) => Promise<{ url: string }>;
}) {
  const detailsId = `evidence-details-${evidence.id}`;

  return (
    <>
      <div className="flex items-center gap-1 px-1.5 py-1">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1.5 py-1 text-left hover:bg-secondary-50"
          aria-expanded={expanded}
          aria-controls={detailsId}
          onClick={onToggle}
        >
          {expanded ? (
            <ChevronDownIcon className="h-4 w-4 shrink-0 text-secondary-500" aria-hidden />
          ) : (
            <ChevronRightIcon className="h-4 w-4 shrink-0 text-secondary-500" aria-hidden />
          )}
          <span className="truncate font-medium text-secondary-900">{evidence.title}</span>
        </button>
        {canWrite ? (
          <div className="flex shrink-0 items-center">
            <EditIconButton label="Editar evidência" disabled={busy} onClick={onEdit} />
            <DeleteIconButton label="Excluir evidência" disabled={busy} onClick={onDelete} />
          </div>
        ) : null}
      </div>
      {expanded ? (
        <div id={detailsId} className="space-y-2 border-t border-secondary-100 px-3 py-2">
          {evidence.description ? (
            <p className="whitespace-pre-wrap text-secondary-700">{evidence.description}</p>
          ) : null}
          {evidence.externalUrl ? (
            <a
              href={evidence.externalUrl}
              target="_blank"
              rel="noreferrer"
              className="block break-all text-primary-600 hover:underline"
            >
              {evidence.externalUrl}
            </a>
          ) : null}
          {evidence.storagePath ? (
            <EvidenceImage
              taskId={taskId}
              evidenceId={evidence.id}
              alt={evidence.title}
              loadImageUrl={loadImageUrl}
            />
          ) : null}
          <p className="text-xs text-secondary-500">{formatDateTimeBr(evidence.createdAt)}</p>
        </div>
      ) : null}
    </>
  );
}

type EvidenceFieldsProps = {
  title: string;
  description: string;
  externalUrl: string;
  imageName: string | null;
  busy: boolean;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onUrlChange: (value: string) => void;
  onImageChange: (file: File | null) => void;
};

function EvidenceFields({
  title,
  description,
  externalUrl,
  imageName,
  busy,
  onTitleChange,
  onDescriptionChange,
  onUrlChange,
  onImageChange,
}: EvidenceFieldsProps) {
  return (
    <>
      <label className="block">
        <span className="erp-label">
          <FieldLabel
            required
            description="Nome curto da evidência, para identificá-la na lista."
            example="Print do fechamento de setembro"
          >
            Título
          </FieldLabel>
        </span>
        <input
          className="erp-input"
          value={title}
          maxLength={200}
          required
          disabled={busy}
          onChange={(event) => onTitleChange(event.target.value)}
        />
      </label>
      <label className="block">
        <span className="erp-label">
          <FieldLabel
            required
            description="O que a evidência comprova."
            example="Planilha conferida com o financeiro e sem divergência de saldo."
          >
            Descrição
          </FieldLabel>
        </span>
        <textarea
          className="erp-input min-h-[64px]"
          value={description}
          maxLength={4000}
          required
          disabled={busy}
          onChange={(event) => onDescriptionChange(event.target.value)}
        />
      </label>
      <div>
        <span className="erp-label">
          <FieldLabel
            description="Opcional. A imagem fica no armazenamento do sistema e pode ser vista nesta evidência."
            example="PNG ou JPG de até 10 MB"
          >
            Imagem
          </FieldLabel>
        </span>
        <EvidenceImageDropzone imageName={imageName} busy={busy} onImageChange={onImageChange} />
      </div>
      <label className="block">
        <span className="erp-label">Link externo (opcional)</span>
        <input
          className="erp-input"
          value={externalUrl}
          disabled={busy}
          maxLength={2000}
          placeholder="https://"
          onChange={(event) => onUrlChange(event.target.value)}
        />
      </label>
    </>
  );
}

function isAcceptedImage(file: File): boolean {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return IMAGE_TYPES.has(file.type) || IMAGE_EXTENSIONS.has(extension);
}

function EvidenceImageDropzone({
  imageName,
  busy,
  onImageChange,
}: {
  imageName: string | null;
  busy: boolean;
  onImageChange: (file: File | null) => void;
}) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (imageName) return;
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
  }, [imageName]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  function acceptFile(file: File | undefined): void {
    if (!file) return;
    if (!isAcceptedImage(file)) {
      toast.error('Envie uma imagem PNG, JPG, GIF ou WebP.');
      return;
    }
    if (file.size <= 0) {
      toast.error('A imagem está vazia.');
      return;
    }
    if (file.size > IMAGE_MAX_BYTES) {
      toast.error('A imagem passa de 10 MB.');
      return;
    }

    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return URL.createObjectURL(file);
    });
    onImageChange(file);
  }

  function clearFile(): void {
    if (inputRef.current) inputRef.current.value = '';
    onImageChange(null);
  }

  function hasFiles(event: DragEvent<HTMLDivElement>): boolean {
    return event.dataTransfer.types.includes('Files');
  }

  function handleDragEnter(event: DragEvent<HTMLDivElement>): void {
    if (busy || !hasFiles(event)) return;
    event.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  }

  function handleDragOver(event: DragEvent<HTMLDivElement>): void {
    if (busy || !hasFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }

  function handleDragLeave(event: DragEvent<HTMLDivElement>): void {
    if (!hasFiles(event)) return;
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>): void {
    if (!hasFiles(event)) return;
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (busy) return;
    const files = event.dataTransfer.files;
    if (files.length > 1) {
      toast.error('Envie uma imagem por vez.');
    }
    acceptFile(files[0]);
  }

  const zoneClass = dragging
    ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
    : 'border-secondary-300 bg-secondary-50/60 hover:bg-secondary-100/80';

  return (
    <div>
      <div
        className={`relative overflow-hidden rounded-xl border border-dashed transition ${zoneClass} ${busy ? 'pointer-events-none opacity-60' : ''}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {imageName ? (
          <div className="flex flex-col items-center gap-2 px-4 py-4 text-center">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt=""
                className="max-h-40 rounded-lg border border-secondary-200 bg-white object-contain"
              />
            ) : null}
            <p className="max-w-full truncate text-sm font-medium text-secondary-800">{imageName}</p>
            <div className="flex items-center gap-3 text-xs font-medium">
              <button
                type="button"
                className="text-primary-600 hover:underline"
                disabled={busy}
                onClick={() => inputRef.current?.click()}
              >
                Trocar
              </button>
              <button
                type="button"
                className="text-red-600 hover:underline"
                disabled={busy}
                onClick={clearFile}
              >
                Remover
              </button>
            </div>
            <p className="text-xs text-secondary-500">Ou arraste outra imagem para substituir</p>
          </div>
        ) : (
          <label
            htmlFor={inputId}
            className="flex min-h-[8.5rem] cursor-pointer flex-col items-center justify-center gap-2 px-4 py-6 text-center"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-secondary-500 shadow-sm ring-1 ring-secondary-200">
              <PhotoIcon className="h-5 w-5" aria-hidden />
            </span>
            <span className="text-sm font-medium text-secondary-800">
              Arraste a imagem ou clique para selecionar
            </span>
            <span className="text-xs text-secondary-500">PNG, JPG, GIF ou WebP de até 10 MB</span>
          </label>
        )}
        {dragging ? (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 bg-primary-50/95 px-4 text-center">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-primary-600 shadow-sm ring-1 ring-primary-200">
              <PhotoIcon className="h-5 w-5" aria-hidden />
            </span>
            <span className="text-sm font-medium text-primary-800">Solte a imagem aqui</span>
          </div>
        ) : null}
      </div>
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        className="sr-only"
        disabled={busy}
        onChange={(event) => {
          acceptFile(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
    </div>
  );
}

function EvidenceImage({
  taskId,
  evidenceId,
  alt,
  loadImageUrl,
}: {
  taskId: string;
  evidenceId: string;
  alt: string;
  loadImageUrl: (taskId: string, evidenceId: string) => Promise<{ url: string }>;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setUrl(null);
    void loadImageUrl(taskId, evidenceId)
      .then((result) => {
        if (!cancelled) setUrl(result.url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [taskId, evidenceId, loadImageUrl]);

  if (failed) {
    return <p className="mt-2 text-xs text-secondary-500">Não foi possível carregar a imagem.</p>;
  }

  if (!url) return null;

  return (
    <a href={url} target="_blank" rel="noreferrer" className="mt-2 block">
      <img src={url} alt={alt} className="max-h-40 rounded-lg border border-secondary-200 object-contain" />
    </a>
  );
}
